import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  chakra,
} from '@chakra-ui/react'
import {
  ThreadWeather,
  defaultThreadWeather,
  threadWeatherOrder,
} from '@rolebase/shared/model/threadWeather'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  ThreadWeatherDoneIcon,
  ThreadWeatherForecastIcon,
  ThreadWeatherRainIcon,
  ThreadWeatherShowerIcon,
  ThreadWeatherStormIcon,
  ThreadWeatherSunnySpellIcon,
} from 'src/icons'
import { threadWeatherColors } from 'src/theme'

export const threadWeatherIcons: Record<
  ThreadWeather,
  (props: { size?: number }) => React.ReactElement
> = {
  [ThreadWeather.Forecast]: ThreadWeatherForecastIcon,
  [ThreadWeather.Storm]: ThreadWeatherStormIcon,
  [ThreadWeather.Rain]: ThreadWeatherRainIcon,
  [ThreadWeather.Shower]: ThreadWeatherShowerIcon,
  [ThreadWeather.SunnySpell]: ThreadWeatherSunnySpellIcon,
}

interface Props {
  // Missing is the normal state: an unclassified tension DISPLAYS as the
  // default and nothing is written for it (REU89-D5).
  value?: ThreadWeather | null
  // A settled tension wears the rainbow instead of its weather: it comes after
  // the rain, so it can never be one of the crans (REU89-D14).
  done?: boolean
  readOnly?: boolean
  onChange?: (value: ThreadWeather) => void
  // The rainbow is a button when this is given (Yohan, 17/09, #106): a click
  // reopens the tension. Absent, or read-only, it stays a plain sign.
  onReopen?: () => void
  // Icon size in px. The line draws it bigger than the menu's icons (Yohan,
  // 17/09, #106): it is what one reads first. See WEATHER_ICON_SIZE.
  size?: number
  // Drawn as a round grey button, like the role and member chips beside it in
  // the add form (Yohan, 17/09, #106).
  isRound?: boolean
}

export default function ThreadWeatherBadge({
  value,
  done,
  readOnly,
  onChange,
  onReopen,
  size,
  isRound,
}: Props) {
  const { t } = useTranslation()
  const weather = value ?? defaultThreadWeather

  if (done) {
    // Read-only stays a plain sign, not a disabled button: a disabled button
    // does not get the hover in every browser, and the tooltip would be lost.
    if (readOnly || !onReopen) {
      return (
        <Tooltip hasArrow placement="top" label={t('ThreadWeather.done')}>
          <span aria-label={t('ThreadWeather.done')}>
            <ThreadWeatherDoneIcon size={size} />
          </span>
        </Tooltip>
      )
    }
    return (
      <Tooltip hasArrow placement="top" label={t('ThreadWeather.reopen')}>
        <chakra.button
          type="button"
          display="flex"
          aria-label={t('ThreadWeather.reopen')}
          onClick={onReopen}
        >
          <ThreadWeatherDoneIcon size={size} />
        </chakra.button>
      </Tooltip>
    )
  }

  const Icon = threadWeatherIcons[weather]
  const color = threadWeatherColors[weather]
  // The shower is grey because it is the ordinary case, and on the round
  // button of the add form that grey sat on a grey background: unreadable
  // (Yohan, 18/09, #106). There, and only there, the ordinary case wears the
  // text colour. The rows keep their grey — their background is the page's.
  const isPlain = color === 'gray'

  return (
    <Menu placement="bottom-start">
      {/* No tooltip on the badge itself (Yohan, 17/09, #106): the name and
          its sentence only come inside the menu, while choosing. */}
      {isRound ? (
        <MenuButton
          as={IconButton}
          isRound
          size="sm"
          isDisabled={readOnly}
          color={isPlain ? 'black' : `${color}.400`}
          _dark={{ color: isPlain ? 'white' : `${color}.400` }}
          aria-label={t(`ThreadWeather.${weather}`)}
          icon={<Icon size={size} />}
        />
      ) : (
        <MenuButton
          display="flex"
          disabled={readOnly}
          color={`${color}.400`}
          aria-label={t(`ThreadWeather.${weather}`)}
        >
          <Icon size={size} />
        </MenuButton>
      )}
      <Portal>
        {!readOnly && onChange && (
          // One row of icons, no names (Yohan, 17/09, #106): the five crans
          // are seen at once, the current one lit in its own colour, and the
          // name with its sentence comes on hover.
          <MenuList zIndex={2000} display="flex" minW="auto" gap={1} p={1}>
            {threadWeatherOrder.map((option) => {
              const OptionIcon = threadWeatherIcons[option]
              const color = threadWeatherColors[option]
              const isCurrent = option === weather
              return (
                <Tooltip
                  key={option}
                  hasArrow
                  placement="top"
                  label={
                    <Box>
                      <Text fontWeight="bold">
                        {t(`ThreadWeather.${option}`)}
                      </Text>
                      <Text>{t(`ThreadWeather.hint.${option}`)}</Text>
                    </Box>
                  }
                >
                  <MenuItem
                    w="auto"
                    p={2}
                    borderRadius="md"
                    borderWidth="2px"
                    color={`${color}.400`}
                    borderColor={isCurrent ? `${color}.400` : 'transparent'}
                    bg={isCurrent ? `${color}.50` : undefined}
                    _dark={{
                      borderColor: isCurrent ? `${color}.300` : 'transparent',
                      bg: isCurrent ? `${color}.900` : undefined,
                    }}
                    role="menuitemradio"
                    aria-checked={isCurrent}
                    aria-label={t(`ThreadWeather.${option}`)}
                    onClick={() => !isCurrent && onChange(option)}
                  >
                    <OptionIcon />
                  </MenuItem>
                </Tooltip>
              )
            })}
          </MenuList>
        )}
      </Portal>
    </Menu>
  )
}
